import type { ShippingId } from '../types/cart';

export type CheckoutStep = 'contact' | 'delivery' | 'payment';

export interface CheckoutForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  shippingId: ShippingId;
  address: string;
  postalCode: string;
  city: string;
  giftWrap: boolean;
  giftMessage: string;
  cardName: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  acceptTerms: boolean;
}

export type FormErrors = Partial<Record<keyof CheckoutForm, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function formatCardNumber(value: string): string {
  return digitsOnly(value).slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

export function formatExpiry(value: string): string {
  const d = digitsOnly(value).slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
}

export function validateStep(step: CheckoutStep, form: CheckoutForm, today: Date): FormErrors {
  const e: FormErrors = {};
  if (step === 'contact') {
    if (!EMAIL.test(form.email.trim())) e.email = 'Indiquez une adresse e-mail valide.';
    if (!form.firstName.trim()) e.firstName = 'Votre prénom est requis.';
    if (!form.lastName.trim()) e.lastName = 'Votre nom est requis.';
    if (form.phone && digitsOnly(form.phone).length < 10) e.phone = 'Numéro incomplet (10 chiffres).';
  }
  if (step === 'delivery' && form.shippingId !== 'pickup') {
    if (!form.address.trim()) e.address = 'L’adresse est requise.';
    if (!/^\d{5}$/.test(form.postalCode.trim())) e.postalCode = 'Code postal à 5 chiffres.';
    if (!form.city.trim()) e.city = 'La ville est requise.';
  }
  if (step === 'payment') {
    if (!form.cardName.trim()) e.cardName = 'Nom du titulaire requis.';
    if (digitsOnly(form.cardNumber).length !== 16) e.cardNumber = 'Numéro de carte incomplet.';
    const m = /^(\d{2})\/(\d{2})$/.exec(form.cardExpiry);
    if (!m || Number(m[1]) < 1 || Number(m[1]) > 12) {
      e.cardExpiry = 'Format MM/AA.';
    } else {
      const month = Number(m[1]);
      const year = 2000 + Number(m[2]);
      const expired = year < today.getFullYear() || (year === today.getFullYear() && month < today.getMonth() + 1);
      if (expired) e.cardExpiry = 'Cette carte a expiré.';
    }
    if (!/^\d{3}$/.test(form.cardCvc)) e.cardCvc = '3 chiffres au dos.';
    if (!form.acceptTerms) e.acceptTerms = 'Merci d’accepter les conditions de vente.';
  }
  return e;
}

export function generateOrderNumber(): string {
  return `EP-${Math.floor(100000 + Math.random() * 900000)}`;
}
